import React from "react";
import {
  Box,
  Typography,
  CircularProgress,
  LinearProgress,
  Grid,
} from "@mui/material";

/**
 * LoanProcessingStatus Component
 * Displays a visual representation of loan processing progress with step indicators
 *
 * @param {number} completionPercentage - The percentage of completion (0-100)
 * @param {boolean} isPolling - Whether the component should be displayed
 * @returns {JSX.Element} The rendered component
 */
export default function LoanProcessingStatus({
  completionPercentage = 33,
  isPolling = false,
}) {
  // Define the steps for the loan processing
  const steps = [
    {
      label: "Passing your details to our Lending Partner",
      threshold: 33,
    },
    {
      label: "Lending Partner updating their records",
      threshold: 66,
    },
    {
      label: "Lending Partner generating KFS",
      threshold: 100,
    },
  ];

  /**
   * Get the status of a step based on completion percentage
   * @param {number} threshold - The threshold for the step
   * @returns {string} The status: 'completed', 'in-progress', or 'pending'
   */
  const getStepStatus = (threshold) => {
    if (completionPercentage >= threshold) {
      return "completed";
    } else if (completionPercentage > threshold - 33) {
      return "in-progress";
    }
    return "pending";
  };

  /**
   * Render the step indicator based on its status
   * @param {string} status - The status of the step
   * @returns {JSX.Element} The step indicator element
   */
  const renderStepIndicator = (status) => {
    switch (status) {
      case "completed":
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 32,
              height: 32,
              borderRadius: "50%",
              backgroundColor: "#4CAF50",
              flexShrink: 0,
            }}
          >
            <Typography sx={{ color: "white", fontSize: "20px" }}>✓</Typography>
          </Box>
        );
      case "in-progress":
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 32,
              height: 32,
              borderRadius: "50%",
              backgroundColor: "#FFA500",
              flexShrink: 0,
            }}
          >
            <CircularProgress size={24} sx={{ color: "white" }} />
          </Box>
        );
      case "pending":
      default:
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 32,
              height: 32,
              borderRadius: "50%",
              backgroundColor: "#FFA500",
              flexShrink: 0,
            }}
          >
            <Typography sx={{ color: "white", fontSize: "18px" }}>◐</Typography>
          </Box>
        );
    }
  };

  if (!isPolling) {
    return null;
  }

  return (
    <Grid item xs={12} sx={{ pt: 4, pb: 2 }}>
      <Grid item sx={{ textAlign: "center", mb: 2 }} xs={10}>
        <Typography
          color="textColor"
          variant="title"
          sx={{ textAlign: "left", fontWeight: "bold", fontFamily: "lato" }}
        >
          Loan Application Approved
        </Typography>
      </Grid>
      <Grid item sx={{ textAlign: "left", mb: 3 }} xs={10}>
        <Typography color="textColor" variant="aadharLabel">
          Good News!
        </Typography>
        <br />
        <Typography color="textColor" variant="body2">
          Your application have been approved by the lender{" "}
          <b>Avanse Financial Services Ltd.</b> This may take a few minutes
          while we complete below steps.
        </Typography>
      </Grid>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {/* Render all steps */}
        {steps.map((step, index) => {
          const status = getStepStatus(step.threshold);
          return (
            <Box
              key={index}
              sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}
            >
              {renderStepIndicator(status)}
              <Typography
                color="textColor"
                variant="body2"
                sx={{ fontWeight: "bold", pt: 0.5 }}
              >
                {step.label}
              </Typography>
            </Box>
          );
        })}

        {/* Progress Bar */}
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Typography
              color="textColor"
              variant="caption"
              sx={{ fontSize: "12px" }}
            >
              {completionPercentage}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={completionPercentage}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: "#E0E0E0",
              "& .MuiLinearProgress-bar": {
                backgroundColor: "#4CAF50",
                borderRadius: 4,
              },
            }}
          />
        </Box>
      </Box>
    </Grid>
  );
}
